"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import SectionTitle from "@/components/SectionTitle";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  animate,
  PanInfo,
} from "framer-motion";
import { useResponsive } from "@/hooks/useResponsive";
import { cn } from "@/utils/cn";

/* =========================================================================
   SECTION 05: MẶT BẰNG & VỊ TRÍ CHIẾN LƯỢC
   - Mapped to ScrollHeader menu item '05: Mặt bằng'
   - Triggers anchor element #mat-bang
   ========================================================================= */
export default function MapSection({
  image,
  mapSubDescription,
  mapDescription,
  mapSubtitle,
  mapTitle,
  trainSubtitle,
  trainTitle,
  onImageClick,
  infrastructureRoutes,
}: {
  image: string;
  mapTitle?: string;
  mapSubDescription?: string;
  mapDescription?: string;
  mapSubtitle?: string;
  trainTitle?: string;
  trainSubtitle?: string;
  onImageClick?: () => void;
  infrastructureRoutes: Array<{
    title: string;
    desc: string;
    image: string;
  }>;
}) {
  const { isMobile } = useResponsive();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);

  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const REAL_COUNT = infrastructureRoutes?.length || 0;
  const itemsPerPage =
    containerWidth < 640 ? 1 : containerWidth < 1024 ? 2 : 4;
  const GAP = 24;
  const itemWidth =
    containerWidth > 0
      ? (containerWidth - (itemsPerPage - 1) * GAP) / itemsPerPage
      : 280;
  const COL_WIDTH = itemWidth + GAP;

  const maxCol = Math.max(0, REAL_COUNT - itemsPerPage);
  const maxScrollX = Math.max(0, maxCol * COL_WIDTH);
  const totalDots = REAL_COUNT > itemsPerPage ? maxCol + 1 : 0;

  useMotionValueEvent(x, "change", (latest) => {
    let col = Math.round(Math.abs(latest) / COL_WIDTH);
    col = Math.max(0, Math.min(col, maxCol));
    setActiveIndex(col);
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setContainerWidth(width);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    setTimeout(() => {
      setIsDragging(false);
    }, 50);

    if (isMobile) return;

    const rawX = x.get();
    const clamped = Math.max(-maxScrollX, Math.min(0, rawX));
    let col = Math.round(Math.abs(clamped) / COL_WIDTH);

    if (
      Math.abs(info.offset.x) > COL_WIDTH * 0.25 ||
      Math.abs(info.velocity.x) > 600
    ) {
      col += info.offset.x < 0 ? 1 : -1;
    }

    col = Math.max(0, Math.min(col, maxCol));
    setActiveIndex(col);
    const target = Math.max(-maxScrollX, Math.min(0, -col * COL_WIDTH));

    animate(x, target, {
      type: "spring",
      stiffness: 320,
      damping: 30,
    });
  };

  const handleGoTo = (idx: number) => {
    if (isDragging) return;
    const clampedIdx = Math.max(0, Math.min(idx, maxCol));
    setActiveIndex(clampedIdx);
    const target = Math.max(-maxScrollX, Math.min(0, -clampedIdx * COL_WIDTH));
    animate(x, target, {
      type: "spring",
      stiffness: 320,
      damping: 30,
    });
  };

  const handlePrev = () => {
    if (isDragging) return;
    const rawX = x.get();
    let col = Math.round(Math.abs(rawX) / COL_WIDTH) - 1;
    col = Math.max(0, col);
    handleGoTo(col);
  };

  const handleNext = () => {
    if (isDragging) return;
    const rawX = x.get();
    let col = Math.round(Math.abs(rawX) / COL_WIDTH) + 1;
    col = Math.min(maxCol, col);
    handleGoTo(col);
  };

  return (
    <section
      className="relative py-12 md:py-16 border-t border-black/5"
      style={{
        background:
          "linear-gradient(to bottom, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 95%, rgba(255, 255, 255, 0) 100%)",
      }}
      id="mat-bang"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* TOP ROW: TEXT LEFT, IMAGE RIGHT */}
        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-20"
          id="lien-ket-vung"
        >
          {/* Left Column: Location & Master Plan Info */}
          <div className="lg:col-span-5 space-y-6">
            <SectionTitle
              title={mapTitle || ""}
              subtitle={mapSubtitle}
              align="left"
              theme="green"
              subtitleColor="primaryHover"
              underlineColor="primaryHover"
            />

            {mapDescription && (
              <p className="text-xs text-black/75 leading-relaxed font-semibold text-justify">
                {mapDescription}
              </p>
            )}
            {mapSubDescription && (
              <div
                className="text-xs text-black/60 leading-relaxed font-medium text-justify"
                dangerouslySetInnerHTML={{ __html: mapSubDescription }}
              />
            )}
          </div>

          {/* Right Column: Master Plan Image with Hotspots */}
          <div className="lg:col-span-7 w-full">
            <div
              className="relative w-full aspect-[992/550] rounded-2xl overflow-hidden shadow-xl border border-black/5 cursor-pointer hover:opacity-98 transition duration-300 group bg-gray-50"
              onClick={onImageClick}
              title="Click để xem hình lớn"
            >
              <SmoothImage
                src={image || "/images/map_dragonhome.png"}
                alt="Master Plan Map"
                fill
                sizes="(max-width: 1024px) 100vw, 60vw"
                className="object-cover transition-transform duration-500 group-hover:scale-[1.01]"
              />
            </div>
          </div>
        </div>

        {/* BOTTOM ROW: INFRASTRUCTURE ROUTES SLIDER */}
        {infrastructureRoutes && infrastructureRoutes.length > 0 && (
          <div className="space-y-6 relative">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <SectionTitle
                title={trainTitle || ""}
                subtitle={trainSubtitle}
                theme="green"
                subtitleColor="primaryHover"
                underlineColor="primaryHover"
                align="left"
                showLine={false}
              />
              {/* Navigation Arrows */}
              <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-auto">
                <button
                  onClick={handlePrev}
                  className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center text-primaryHover hover:bg-primaryHover hover:text-white transition-all duration-300 active:scale-95 shadow-sm hover:shadow-md"
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={handleNext}
                  className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center text-primaryHover hover:bg-primaryHover hover:text-white transition-all duration-300 active:scale-95 shadow-sm hover:shadow-md"
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="relative w-full overflow-hidden -mx-2 px-2 -my-3 py-3">
              <div
                ref={containerRef}
                className="w-full overflow-x-hidden overflow-y-visible py-2"
              >
                <motion.div
                  className="slider flex gap-6"
                  drag="x"
                  dragDirectionLock
                  dragMomentum
                  dragElastic={0.08}
                  dragTransition={{ power: 0.2, timeConstant: 200 }}
                  dragConstraints={{ left: -maxScrollX, right: 0 }}
                  style={{ x }}
                  onDragStart={() => setIsDragging(true)}
                  onDragEnd={handleDragEnd}
                >
                  {infrastructureRoutes.map((route, idx) => {
                    const isEven = idx % 2 === 0;
                    return (
                      <div
                        key={idx}
                        style={{ width: `${itemWidth}px` }}
                        className="shrink-0 bg-white rounded-2xl overflow-hidden flex flex-col h-[330px] shadow-sm border border-black/5 group cursor-pointer transition-all duration-500 ease-out hover:shadow-xl hover:-translate-y-1.5 select-none"
                      >
                        {isEven ? (
                          <>
                            {/* Image Top */}
                            <div className="relative aspect-[4/3] w-full bg-black/5 overflow-hidden">
                              <SmoothImage
                                src={route.image}
                                alt={route.title}
                                fill
                                sizes="300px"
                                className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                draggable={false}
                              />
                            </div>
                            {/* Text Bottom */}
                            <div className="p-5 flex flex-col flex-1 justify-center">
                              <h5 className="font-extrabold text-xs text-primary uppercase tracking-wide mb-1.5 transition-colors duration-300 group-hover:text-primaryHover">
                                {route.title}
                              </h5>
                              <p className="text-[11px] text-black/60 leading-relaxed font-semibold line-clamp-3">
                                {route.desc}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Text Top */}
                            <div className="p-5 flex flex-col flex-1 justify-center">
                              <h5 className="font-extrabold text-xs text-primary uppercase tracking-wide mb-1.5 transition-colors duration-300 group-hover:text-primaryHover">
                                {route.title}
                              </h5>
                              <p className="text-[11px] text-black/60 leading-relaxed font-semibold line-clamp-3">
                                {route.desc}
                              </p>
                            </div>
                            {/* Image Bottom */}
                            <div className="relative aspect-[4/3] w-full bg-black/5 overflow-hidden">
                              <SmoothImage
                                src={route.image}
                                alt={route.title}
                                fill
                                sizes="300px"
                                className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                draggable={false}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </motion.div>
              </div>

              {/* Pagination Dots */}
              {totalDots > 1 && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  {Array.from({ length: totalDots }).map((_, idx) => {
                    const isActive = activeIndex === idx;
                    return (
                      <button
                        key={idx}
                        onClick={() => handleGoTo(idx)}
                        className={cn(
                          "h-2 rounded-full transition-all duration-300 cursor-pointer focus:outline-none",
                          isActive
                            ? "w-7 bg-primary"
                            : "w-2 bg-black/20 hover:bg-black/40",
                        )}
                        aria-label={`Go to slide ${idx + 1}`}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
